import { Table, TableBody, TableCell, TableRow } from "@mui/material";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import PATDeleteButton from "../../components/Buttons/PATDeleteButton";
import PATEditButton from "../../components/Buttons/PATEditButton";
import { useDispatch, useSelector } from "react-redux";
import { updateOrCreateUserSetting } from "../../reducers/UserSettingsReducer";
import { getRowsPerPageOptions } from "../../utils/functions";
import { Box } from "@mui/system";
import { useTheme } from "@emotion/react";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { AMSModules, paginationStyle } from "../../utils/constants";
import DispensableItemsTableToolbar from "./DispensableItemsTableToolbar";
import AddEditDispensableItemDialog from "./AddEditDispensableItemDialog";
import IssueItemDialog from "./IssueItemDialog";
import RestockItemDialog from "./RestockItemDialog";
import {
	getDispensableItems,
	createDispensableItem,
	updateDispensableItem,
	deleteDispensableItem,
	createIssuance,
	restockItem,
} from "../../reducers/DispensableItemsReducer";
import { notify } from "../../reducers/NotifierReducer";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";
import { getEmployees, getFundingSources } from "../../utils/commonServices";
import { getVehicleOptions } from "../WorkOrders/useWorkOrdersData";
import PATUpButton from "../../components/Buttons/PATUpButton";
import PATDownButton from "../../components/Buttons/PATDownButton";

const INITIAL_PAGE = 0;
const INITIAL_PAGE_SIZE = 20;
const INITIAL_SORT_MODEL = [{ field: "id", sort: "desc" }];

export const initialFilterState = {
	vendor: [],
	lowStock: false,
};

export const DispensableItemsTypes = {
	ITEMS: "Items",
	RESTOCK: "Restock",
	ISSUANCES: "Issuances",
};

const DispensableItems = () => {
	const dispatch = useDispatch();
	const theme = useTheme();

	const { items, loading, total, lowStockCount } = useSelector(
		(state) =>
			state?.dispensableItems || {
				items: [],
				loading: false,
				total: 0,
			},
	);
	const userAgency = useSelector((state) => state.user.userAgency);
	const { userData, isAuthenticated } = useSelector((state) => state.user);
	const { userSettings } = useSelector((state) => state.userSettings);
	const themeMode = useSelector((state) => state.theme.mode);
	const filtersInfo = useSelector((state) => state.filtersInfo);

	const [search, setSearch] = useState("");
	const [itemType, setItemType] = useState(DispensableItemsTypes.ITEMS);
	const [columnsOrder, setColumnsOrder] = useState([]);
	const [columnsSize, setColumnsSize] = useState([]);
	const [settingsLoaded, setSettingsLoaded] = useState(false);
	const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
	const [page, setPage] = useState(INITIAL_PAGE);
	const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
	const [sortModel, setSortModel] = useState(INITIAL_SORT_MODEL);
	const [filters, setFilters] = useState(
		filtersInfo?.dispensableItems || initialFilterState,
	);
	const [openDialog, setOpenDialog] = useState(false);
	const [openIssueDialog, setOpenIssueDialog] = useState(false);
	const [openRestockDialog, setOpenRestockDialog] = useState(false);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [selectedItem, setSelectedItem] = useState(null);
	const [selectedId, setSelectedId] = useState(null);
	const [employees, setEmployees] = useState([]);
	const [vehicles, setVehicles] = useState([]);
	const [fundingSources, setFundingSources] = useState([]);

	const currentAgency = useRef(null);
	const previousItemType = useRef(itemType);

	const handleLoadData = async () => {
		const [empList, vehicleList, fundingList] = await Promise.all([
			getEmployees(userAgency, true),
			getVehicleOptions(userAgency),
			getFundingSources(userAgency, true),
		]);

		setEmployees(empList);
		setVehicles(vehicleList);
		setFundingSources(fundingList);
	};

	useEffect(() => {
		if (userAgency) {
			handleLoadData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userAgency]);

	// Get employee info for display
	const getEmployeeDisplay = useCallback(
		(employeeId) => {
			const employee = employees.find(
				(e) => e.value === employeeId || e.value === String(employeeId),
			);
			return employee ? employee.label : "—";
		},
		[employees],
	);

	// Get vehicle info for display
	const getVehicleDisplay = useCallback(
		(vehicleId) => {
			const vehicle = vehicles.find((v) => v.value === vehicleId);
			return vehicle ? vehicle.label : vehicleId || "—";
		},
		[vehicles],
	);

	// Items columns (also used for Restock view)
	const itemColumns = useMemo(
		() => [
			{
				field: "trackingNumber",
				headerName: "Tracking",
				width: 150,
				sortable: true,
			},
			{
				field: "description",
				headerName: "Description",
				width: 300,
				sortable: true,
			},
			{
				field: "vendor",
				headerName: "Vendor",
				width: 150,
				sortable: true,
			},
			{
				field: "cost",
				headerName: "Unit Cost",
				width: 120,
				sortable: true,
				renderCell: ({ row }) =>
					row.cost != null ? `$${Number(row.cost).toFixed(2)}` : "—",
			},
			{
				field: "unitsInStock",
				headerName: "In Stock",
				width: 100,
				sortable: true,
				renderCell: ({ row }) => {
					const stock = row.unitsInStock || 0;
					const min = row.minBeforeRestock || 0;
					const color =
						stock === 0
							? "error"
							: stock <= min
								? "warning"
								: "success";
					return (
						<span
							style={{
								fontWeight: 700,
								color: `var(--${color})`,
							}}
						>
							{stock}
						</span>
					);
				},
			},
			{
				field: "maxStock",
				headerName: "Max",
				width: 80,
				sortable: true,
				renderCell: ({ row }) => row.maxStock || "—",
			},
			{
				field: "minBeforeRestock",
				headerName: "Min",
				width: 80,
				sortable: true,
				renderCell: ({ row }) => row.minBeforeRestock || "—",
			},
			{
				field: "actions",
				headerName: "Actions",
				type: "actions",
				width: 150,
				align: "center",
				sortable: false,
				renderCell: ({ row }) => {
					if (!row.id) return null;

					// For Restock view, show only Restock button
					if (itemType === DispensableItemsTypes.RESTOCK) {
						return (
							<Table>
								<TableBody>
									<TableRow>
										<TableCell
											align="center"
											sx={{ padding: 0.5 }}
										>
											<PATDownButton
												tooltipText="Restock Item"
												onClick={(e) => {
													e.stopPropagation();
													setSelectedItem(row);
													setOpenRestockDialog(true);
												}}
												size="20px"
											/>
										</TableCell>
									</TableRow>
								</TableBody>
							</Table>
						);
					}

					// For Items view, show Issue, Edit, Delete
					return (
						<Table>
							<TableBody>
								<TableRow>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										<PATUpButton
											tooltipText="Issue Item"
											onClick={(e) => {
												e.stopPropagation();
												setSelectedItem(row);
												setOpenIssueDialog(true);
											}}
											size="20px"
										/>
									</TableCell>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										<PATEditButton
											tooltipText="Edit Item"
											onClick={(e) => {
												e.stopPropagation();
												setSelectedItem(row);
												setOpenDialog(true);
											}}
											size="20px"
										/>
									</TableCell>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										<PATDeleteButton
											tooltipText="Delete Item"
											onClick={(e) => {
												e.stopPropagation();
												setSelectedId(row.id);
												setSelectedItem(row);
												setOpenDeleteDialog(true);
											}}
											size="20px"
										/>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					);
				},
			},
		],
		[itemType],
	);

	// Issuances columns
	const issuanceColumns = useMemo(
		() => [
			{
				field: "date",
				headerName: "Date",
				width: 120,
				sortable: true,
				renderCell: ({ row }) =>
					row.date ? new Date(row.date).toLocaleDateString() : "—",
			},
			{
				field: "trackingNumber",
				headerName: "Item",
				width: 150,
				sortable: true,
			},
			{
				field: "description",
				headerName: "Description",
				width: 250,
				sortable: true,
			},
			{
				field: "perPersonnelId",
				headerName: "Employee",
				width: 180,
				sortable: true,
				renderCell: ({ row }) =>
					row.isRestock
						? "—"
						: getEmployeeDisplay(row.perPersonnelId),
			},
			{
				field: "vehicleId",
				headerName: "Vehicle",
				width: 150,
				sortable: true,
				renderCell: ({ row }) =>
					row.vehicleId ? getVehicleDisplay(row.vehicleId) : "—",
			},
			{
				field: "quantity",
				headerName: "Qty",
				width: 80,
				sortable: true,
				renderCell: ({ row }) => {
					if (row.isRestock) {
						return (
							<span
								style={{
									color: "var(--success)",
									fontWeight: 700,
								}}
							>
								+{Math.abs(row.quantity)}
							</span>
						);
					}
					return row.quantity || "—";
				},
			},
			{
				field: "serialId",
				headerName: "Serial/ID",
				width: 120,
				sortable: true,
				renderCell: ({ row }) => row.serialId || "—",
			},
			{
				field: "totalCost",
				headerName: "Total Cost",
				width: 120,
				sortable: true,
				renderCell: ({ row }) =>
					row.totalCost != null
						? `$${Number(row.totalCost).toFixed(2)}`
						: "—",
			},
			{
				field: "isRestock",
				headerName: "Type",
				width: 100,
				sortable: true,
				renderCell: ({ row }) =>
					row.isRestock ? (
						<span
							style={{
								fontSize: 11,
								fontWeight: 700,
								color: "var(--primary)",
							}}
						>
							RESTOCK
						</span>
					) : (
						<span
							style={{
								fontSize: 11,
								color: "var(--text2)",
							}}
						>
							Issue
						</span>
					),
			},
		],
		[getEmployeeDisplay, getVehicleDisplay],
	);

	const baseColumns = useMemo(() => {
		return itemType === DispensableItemsTypes.ISSUANCES
			? issuanceColumns
			: itemColumns;
	}, [itemType, issuanceColumns, itemColumns]);

	useEffect(() => {
		if (previousItemType.current !== itemType) {
			// Reset page
			setPage(INITIAL_PAGE);

			// Reset sort model to default
			setSortModel(INITIAL_SORT_MODEL);

			// Reset column visibility for new column set
			const newVisibility = {};
			baseColumns.forEach((col) => {
				newVisibility[col.field] = true;
			});
			setColumnVisibilityModel(newVisibility);

			// Reset column order
			setColumnsOrder([]);

			// Update previous itemType
			previousItemType.current = itemType;
		}
	}, [itemType, baseColumns]);

	useEffect(() => {
		if (!settingsLoaded || !isAuthenticated) return;

		const { field, sort } = sortModel[0] || {};
		if (field && sort) {
			const isAgencyChanged = currentAgency.current !== userAgency;
			if (isAgencyChanged) {
				resetFilters();
				currentAgency.current = userAgency;
			}

			dispatch(
				getDispensableItems(
					page,
					pageSize,
					field,
					sort,
					search,
					isAgencyChanged ? initialFilterState : filters,
					userAgency,
					itemType,
				),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		dispatch,
		page,
		pageSize,
		sortModel,
		search,
		filters,
		settingsLoaded,
		isAuthenticated,
		userAgency,
		itemType,
	]);

	// Build final columns with order and sizing
	const columns = useMemo(() => {
		const hasOrder = columnsOrder.length > 0;
		const hasSizes = Object.keys(columnsSize).length > 0;

		if (!hasOrder && !hasSizes) return baseColumns;

		let baseOrderedColumns;

		if (hasOrder) {
			// Filter columnsOrder to only include valid fields for current view
			const validFields = baseColumns.map((col) => col.field);
			const validOrder = columnsOrder.filter((field) =>
				validFields.includes(field),
			);

			const ordered = validOrder
				.map((field) => baseColumns.find((col) => col.field === field))
				.filter(Boolean);

			const remaining = baseColumns.filter(
				(col) => !validOrder.includes(col.field),
			);

			const actionsIndex = ordered.findIndex(
				(col) => col.field === "actions",
			);
			const insertAt = actionsIndex > -1 ? actionsIndex : ordered.length;

			baseOrderedColumns = [
				...ordered.slice(0, insertAt),
				...remaining,
				...ordered.slice(insertAt),
			];
		} else {
			baseOrderedColumns = baseColumns;
		}

		const finalColumns = baseOrderedColumns.map((col) => ({
			...col,
			width: columnsSize[col.field] ?? col.width ?? 100,
		}));

		return finalColumns;
	}, [baseColumns, columnsOrder, columnsSize]);

	const allColumnFields = baseColumns.map((col) => col.field);

	useEffect(() => {
		const defaultVisibility = {};
		currentAgency.current = userAgency;
		allColumnFields.forEach((field) => {
			defaultVisibility[field] = true;
		});
		setColumnVisibilityModel((prev) => ({ ...defaultVisibility, ...prev }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!isAuthenticated || settingsLoaded) return;

		const initialize = async () => {
			const settingsMap = Object.fromEntries(
				userSettings.map((s) => [s.key, s.value]),
			);
			if (settingsMap["dispensableItemsTableColumnOrder"]) {
				setColumnsOrder(
					JSON.parse(settingsMap["dispensableItemsTableColumnOrder"]),
				);
			}

			if (settingsMap["dispensableItemsTableColumnSizes"]) {
				const widthArray = JSON.parse(
					settingsMap["dispensableItemsTableColumnSizes"],
				);
				const result = widthArray.reduce((acc, curr) => {
					acc[curr.field] = curr.width;
					return acc;
				}, {});
				setColumnsSize(result);
			}

			if (settingsMap["dispensableItemsTableColumnVisibility"]) {
				setColumnVisibilityModel(
					JSON.parse(
						settingsMap["dispensableItemsTableColumnVisibility"],
					),
				);
			}

			if (settingsMap["dispensableItemsTableSortModel"]) {
				const savedSortModel = JSON.parse(
					settingsMap["dispensableItemsTableSortModel"],
				);
				if (Array.isArray(savedSortModel)) {
					const validSortModel = savedSortModel.filter((model) =>
						baseColumns.some((col) => col.field === model.field),
					);
					if (validSortModel.length > 0) {
						setSortModel(savedSortModel);
					}
				}
			}
			setSettingsLoaded(true);
		};

		initialize();
	}, [isAuthenticated, userSettings, settingsLoaded, baseColumns]);

	const handleSortModelChange = useCallback(
		(model) => {
			const newSortModel = model.length > 0 ? model : INITIAL_SORT_MODEL;

			// Validate that the sort field exists in current columns
			const validSortModel = newSortModel.filter((m) =>
				baseColumns.some((col) => col.field === m.field),
			);

			const finalSortModel =
				validSortModel.length > 0 ? validSortModel : INITIAL_SORT_MODEL;

			const isSortModelDifferent =
				JSON.stringify(finalSortModel) !== JSON.stringify(sortModel);

			if (isSortModelDifferent) {
				setSortModel(finalSortModel);
				setPage(INITIAL_PAGE);

				if (userData?.user?.ptsUserID) {
					dispatch(
						updateOrCreateUserSetting(
							userData.user.ptsUserID,
							"dispensableItemsTableSortModel",
							JSON.stringify(finalSortModel),
						),
					);
				}
			}
		},
		[sortModel, dispatch, userData?.user?.ptsUserID, baseColumns],
	);

	const handleColumnOrderChange = useCallback(
		(params, event, details) => {
			const newColumnOrder = details.api
				.getAllColumns()
				.map((col) => col.field);
			if (userData?.user?.ptsUserID) {
				dispatch(
					updateOrCreateUserSetting(
						userData.user.ptsUserID,
						"dispensableItemsTableColumnOrder",
						JSON.stringify(newColumnOrder),
					),
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID],
	);

	const handleColumnWidthChange = (params, event, details) => {
		const columnSizes = details.api.getAllColumns().map((col) => ({
			field: col.field,
			width: col.width,
		}));
		if (userData?.user?.ptsUserID) {
			dispatch(
				updateOrCreateUserSetting(
					userData.user.ptsUserID,
					"dispensableItemsTableColumnSizes",
					JSON.stringify(columnSizes),
				),
			);
		}
	};

	const handleColumnVisibilityModelChange = useCallback(
		(newModel) => {
			setColumnVisibilityModel(newModel);
			if (userData?.user?.ptsUserID) {
				dispatch(
					updateOrCreateUserSetting(
						userData.user.ptsUserID,
						"dispensableItemsTableColumnVisibility",
						JSON.stringify(newModel),
					),
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID],
	);

	const rowsPerPageOptions = useMemo(
		() => getRowsPerPageOptions(total),
		[total],
	);

	const refreshList = useCallback(() => {
		// Get current sort model
		const { field, sort } = sortModel[0] || {};
		if (field && sort) {
			dispatch(
				getDispensableItems(
					page,
					pageSize,
					field,
					sort,
					search,
					filters,
					userAgency,
					itemType,
				),
			);
		}
	}, [
		dispatch,
		page,
		pageSize,
		sortModel,
		search,
		filters,
		userAgency,
		itemType,
	]);

	const handleOnFilterChange = (newFilters) => {
		setFilters(newFilters);
		dispatch({ type: AMSModules.DispensableItems, payload: newFilters });
	};

	const resetFilters = () => {
		setFilters(initialFilterState);
		dispatch({
			type: AMSModules.DispensableItems,
			payload: initialFilterState,
		});
	};

	// Handle save item
	const handleSaveItem = async (payload) => {
		try {
			if (selectedItem) {
				await dispatch(
					updateDispensableItem(
						selectedItem.id,
						payload,
						userAgency,
						userData.user.ptsUserID,
						selectedItem,
					),
				);
			} else {
				await dispatch(
					createDispensableItem(
						payload,
						userAgency,
						userData.user.ptsUserID,
					),
				);
			}
			setOpenDialog(false);
			setSelectedItem(null);
			dispatch(
				notify(
					`Item ${selectedItem ? "updated" : "created"} successfully`,
					"success",
				),
			);
			refreshList();
		} catch (error) {
			dispatch(notify("Failed to save item", "error"));
		}
	};

	// Handle issue item
	const handleIssueItem = async (payload) => {
		try {
			await dispatch(
				createIssuance(
					selectedItem.id,
					payload,
					userAgency,
					userData.user.ptsUserID,
					selectedItem,
				),
			);
			setOpenIssueDialog(false);
			setSelectedItem(null);
			dispatch(notify("Item issued successfully", "success"));
			refreshList();
		} catch (error) {
			dispatch(notify("Failed to issue item", "error"));
		}
	};

	// Handle restock item
	const handleRestockItem = async (payload) => {
		try {
			await dispatch(
				restockItem(
					selectedItem.id,
					payload,
					userAgency,
					userData.user.ptsUserID,
				),
			);
			setOpenRestockDialog(false);
			setSelectedItem(null);
			dispatch(notify("Item restocked successfully", "success"));
			refreshList();
		} catch (error) {
			dispatch(notify("Failed to restock item", "error"));
		}
	};

	// Handle delete
	const handleDelete = async () => {
		try {
			await dispatch(
				deleteDispensableItem(
					selectedId,
					userData.user.ptsUserID,
					userAgency,
					selectedItem,
				),
			);
			setOpenDeleteDialog(false);
			setSelectedId(null);
			setSelectedItem(null);
			dispatch(notify("Item deleted successfully", "success"));
			refreshList();
		} catch (error) {
			dispatch(notify("Failed to delete item", "error"));
		}
	};

	return (
		<>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "calc(100vh - 85px)",
					marginTop: theme.spacing(0.5),
				}}
			>
				<DispensableItemsTableToolbar
					openAdd={() => {
						setSelectedItem(null);
						setOpenDialog(true);
					}}
					search={search}
					setSearch={setSearch}
					itemType={itemType}
					setItemType={setItemType}
					resetFilters={resetFilters}
					filters={filters}
					onFilterChange={handleOnFilterChange}
					lowStockCount={lowStockCount}
					items={items}
					employees={employees}
					vehicles={vehicles}
				/>

				<Box
					sx={{
						flex: 1,
						overflow: "hidden",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<DataGridPro
						key={itemType}
						rows={items}
						columns={columns}
						initialState={{ pinnedColumns: { right: ["actions"] } }}
						getRowId={(row) => row.GUID || row.id}
						columnVisibilityModel={columnVisibilityModel}
						onColumnVisibilityModelChange={
							handleColumnVisibilityModelChange
						}
						pageSize={pageSize}
						rowsPerPageOptions={rowsPerPageOptions}
						pagination
						paginationMode="server"
						sortModel={sortModel}
						sortingMode="server"
						onSortModelChange={handleSortModelChange}
						rowCount={total}
						page={page}
						onPageChange={setPage}
						onPageSizeChange={setPageSize}
						loading={loading}
						disableSelectionOnClick
						onColumnOrderChange={handleColumnOrderChange}
						onColumnWidthChange={handleColumnWidthChange}
						sx={{
							...paginationStyle(theme, themeMode),
							color: theme.palette.colorFont,
							margin: 1,
						}}
					/>
				</Box>
			</Box>

			<AddEditDispensableItemDialog
				open={openDialog}
				onClose={() => {
					setOpenDialog(false);
					setSelectedItem(null);
				}}
				onSave={handleSaveItem}
				initialData={selectedItem}
				employees={employees}
				vehicles={vehicles}
				fundingSourceOptions={fundingSources}
			/>

			<IssueItemDialog
				open={openIssueDialog}
				onClose={() => {
					setOpenIssueDialog(false);
					setSelectedItem(null);
				}}
				onSave={handleIssueItem}
				issueItem={selectedItem}
				employeeOptions={employees}
				vehicleOptions={vehicles}
			/>

			<RestockItemDialog
				open={openRestockDialog}
				onClose={() => {
					setOpenRestockDialog(false);
					setSelectedItem(null);
				}}
				onSave={handleRestockItem}
				item={selectedItem}
				fundingSourceOptions={fundingSources}
			/>

			<DeleteConfirmationDialog
				open={openDeleteDialog}
				onClose={() => {
					setOpenDeleteDialog(false);
					setSelectedId(null);
					setSelectedItem(null);
				}}
				onConfirm={handleDelete}
				title="Delete Dispensable Item"
				message="Are you sure you want to delete this item? This will also delete all associated issuance records."
			/>
		</>
	);
};

export default DispensableItems;
